import React from 'react';
import PropTypes from 'prop-types';
import {
  Link as ReactRouterDomLink,
  NavLink as ReactRouterDomNavLink,
} from 'react-router-dom';
import {
  getElementIdFromHash,
  getElementIdFromPathName,
  hasHashPart,
} from './utils';

/*
Inspired by "react-router-hash-link" module, this is a link that upon clicking it, it scrolls to the relevant element.
The relevant element is determined like this: the pathname suffix of the link's url is taken, (i.e "authentication"
from "/getting-started/authentication") and the element with the matching id is scrolled into view
 (i.e scroll to the element with id of "authentication").
 */

let elementIdToScrollTo = '';
let observer = null;
let asyncTimerId = null;
let scrollFunction = null;

function reset() {
  elementIdToScrollTo = '';
  if (observer !== null) {
    observer.disconnect();
  }
  if (asyncTimerId !== null) {
    window.clearTimeout(asyncTimerId);
    asyncTimerId = null;
  }
}

function getElAndScroll() {
  const element = document.getElementById(elementIdToScrollTo);
  if (element !== null) {
    scrollFunction(element);
    reset();
    return true;
  }
  return false;
}

function linkScroll() {
  // Push onto callback queue so it runs after the DOM is updated
  window.setTimeout(() => {
    if (getElAndScroll() === false) {
      if (observer === null) {
        observer = new MutationObserver(getElAndScroll);
      }
      observer.observe(document, {
        attributes: true,
        childList: true,
        subtree: true,
      });
      // if the element doesn't show up in 10 seconds, stop checking
      asyncTimerId = window.setTimeout(() => {
        reset();
      }, 10000);
    }
  }, 0);
}

function updateElementIdToScrollToHashPart(props) {
  if (typeof props.to === 'string') {
    elementIdToScrollTo = getElementIdFromHash(props.to);
  } else if (
    typeof props.to === 'object' &&
    typeof props.to.hash === 'string'
  ) {
    elementIdToScrollTo = props.to.hash.replace('#', '');
  }
}

function updateElementIdToScrollToByPathnamePart(props) {
  if (typeof props.to === 'string') {
    elementIdToScrollTo = getElementIdFromPathName(props.to);
  } else if (
    typeof props.to === 'object' &&
    typeof props.to.pathname === 'string'
  ) {
    elementIdToScrollTo = getElementIdFromPathName(props.to.pathname);
  }
}

function scrollToElementIfFound(props) {
  window.isScrollFromLibraryView = true;
  if (elementIdToScrollTo) {
    scrollFunction =
      props.scroll ||
      ((el) =>
        props.smooth
          ? el && el.scrollIntoView({ behavior: 'smooth' })
          : el && el.scrollIntoView());
    linkScroll();
  }
}

export function genericLink(props, As) {
  const handleClick = (e) => {
    reset();
    if (e.metaKey || e.ctrlKey) {
      // Support ctrl+click (cmd+click in Mac) in links. Meaning that it shouldn't run JS, but just open the link in new tab.
      return;
    }
    if (props.onClick) {
      props.onClick(e);
    }
    if (hasHashPart(props.to)) {
      updateElementIdToScrollToHashPart(props);
    } else {
      updateElementIdToScrollToByPathnamePart(props);
    }
    scrollToElementIfFound(props);
  };
  const { scroll, smooth, ...filteredProps } = props;
  return (
    <As {...filteredProps} onClick={handleClick}>
      {props.children}
    </As>
  );
}

export function Link(props) {
  return genericLink(props, ReactRouterDomLink);
}

export function NavLink(props) {
  return genericLink(props, ReactRouterDomNavLink);
}

const propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  scroll: PropTypes.func,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

Link.propTypes = propTypes;
NavLink.propTypes = propTypes;
